import { useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import type { GeneralFormProps } from '@/types/general';

import { Checkbox } from '../ui/checkbox';

type Props = GeneralFormProps & {
  /**
   * Canonical helper text shown beneath the label.
   * Falls back to `description` for backward compatibility.
   */
  helperText?: string;
};

function RHFShadcnCheckbox({
  name,
  title,
  description,
  helperText,
  value: externalValue,
  required,
  onChange: externalOnChange,
  disabled,
}: Props) {
  const { control } = useFormContext();
  const resolvedHelperText = helperText ?? description;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const checkboxId = `checkbox-${field.name.replace(/\./g, '-')}`;

        return (
          <FormItem className="w-full">
            <div className="flex items-center gap-3">
              <FormControl>
                <Checkbox
                  id={checkboxId}
                  disabled={disabled}
                  required={required}
                  data-testid={field.name}
                  className={disabled === true ? 'cursor-not-allowed opacity-50' : ''}
                  onCheckedChange={(e) => {
                    if (externalOnChange !== undefined) {
                      externalOnChange(e);
                      return;
                    }
                    field.onChange(e);
                  }}
                  checked={field.value === true || externalValue === true}
                />
              </FormControl>
              <div className="flex flex-col">
                {title !== undefined && title !== '' && (
                  <FormLabel
                    htmlFor={checkboxId}
                    className={
                      disabled === true ? 'cursor-not-allowed' : 'cursor-pointer'
                    }
                  >
                    {title}
                    {required === true && (
                      <span aria-label="required" className="text-error ml-1">*</span>
                    )}
                  </FormLabel>
                )}
                {resolvedHelperText !== undefined && resolvedHelperText !== '' && (
                  <FormDescription className="mt-1 type-body-s text-on-surface-variant">
                    {resolvedHelperText}
                  </FormDescription>
                )}
              </div>
            </div>
            <FormMessage className="mt-1.5" />
          </FormItem>
        );
      }}
    />
  );
}

export default RHFShadcnCheckbox;
